import { useState, useRef } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Camera, MapPin, Check } from "lucide-react";
import { api, photoUrl } from "../lib/api";
import { detectLocation } from "../lib/geo";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/useLang";
import { getSetup, getAppStrings, GENDER_VALUES, LOOKING_VALUES, LOCATION_PREFS } from "../i18n/appI18n";

const field =
  "w-full bg-black/40 border border-reveal-rose/40 rounded-xl px-4 py-3 font-body text-reveal-cream placeholder:text-reveal-cream/40 outline-none focus:border-reveal-rose transition-colors";
const AGES = Array.from({ length: 48 }, (_, i) => 18 + i);
const STEPS = ["photo", "details", "location", "gender", "lookingFor", "ageRange", "locationPref", "bio"];

export default function ProfileSetup({ onComplete }) {
  const { refresh } = useAuth();
  const lang = useLang();
  const s = getSetup(lang);
  const px = getAppStrings(lang).profileExtra;
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const inputRef = useRef(null);
  const [form, setForm] = useState({
    display_name: "", age: "", city: "", country: "", country_name: "",
    latitude: null, longitude: null, gender: "", looking_for: "",
    min_age: 18, max_age: 65, location_pref: "my_country", bio: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const key = STEPS[step];
  const st = s.steps[key];

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const primary = (data.photos || []).find((p) => p.primary) || (data.photos || [])[0];
      setPhoto(primary?.path || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const detect = async () => {
    setDetecting(true);
    try {
      const loc = await detectLocation();
      set("latitude", loc.latitude); set("longitude", loc.longitude);
      setForm((f) => ({ ...f, latitude: loc.latitude, longitude: loc.longitude, city: loc.city || f.city, country: loc.country, country_name: loc.country_name }));
      if (!loc.city) toast.message(st.denied);
    } catch {
      toast.message(st.denied);
    } finally {
      setDetecting(false);
    }
  };

  const canProceed = () => {
    if (key === "details") return form.display_name.trim() && form.age;
    if (key === "gender") return !!form.gender;
    if (key === "lookingFor") return !!form.looking_for;
    return true;
  };

  const next = async () => {
    if (!canProceed()) return;
    if (step < STEPS.length - 1) { setStep((x) => x + 1); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: form.age ? parseInt(form.age, 10) : null,
        min_age: parseInt(form.min_age, 10),
        max_age: parseInt(form.max_age, 10),
      };
      const { data } = await api.put("/profile", payload);
      await refresh();
      onComplete?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save. Please try again.");
      setSaving(false);
    }
  };

  const back = () => setStep((x) => Math.max(0, x - 1));

  return (
    <div className="min-h-screen bg-reveal-black text-reveal-cream font-body px-5 py-10" data-testid="profile-setup-page">
      <div className="max-w-md mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="font-heading text-2xl text-reveal-rose">Reveal</span>
            <span className="text-xs text-reveal-cream/40" data-testid="setup-progress">{step + 1} / {STEPS.length}</span>
          </div>
          <div className="h-1 rounded-full bg-reveal-rose/15 overflow-hidden">
            <div className="h-full bg-reveal-rose transition-all duration-300" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>
        </div>

        <h2 className="font-heading text-3xl text-reveal-cream mb-1" data-testid="setup-step-title">{st.title}</h2>
        {st.sub && <p className="text-sm text-reveal-cream/55 mb-7">{st.sub}</p>}
        {!st.sub && <div className="mb-7" />}

        {key === "photo" && (
          <div>
            <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="setup-add-photo-button"
              className="w-40 h-40 mx-auto rounded-2xl border border-dashed border-reveal-rose/40 flex flex-col items-center justify-center gap-2 text-reveal-cream/50 hover:text-reveal-rose hover:border-reveal-rose/70 transition-colors overflow-hidden">
              {photo ? <img src={photoUrl(photo)} alt="" className="w-full h-full object-cover" /> : <><Camera className="w-7 h-7" /><span className="text-xs">{uploading ? st.uploading : st.add}</span></>}
            </button>
            <input ref={inputRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="setup-photo-input" />
          </div>
        )}

        {key === "details" && (
          <div className="space-y-4">
            <input value={form.display_name} onChange={(e) => set("display_name", e.target.value)} placeholder={st.name} data-testid="setup-name-input" className={field} />
            <input type="number" min="18" max="120" value={form.age} onChange={(e) => set("age", e.target.value)} placeholder={st.age} data-testid="setup-age-input" className={field} />
          </div>
        )}

        {key === "location" && (
          <div className="space-y-4">
            <button type="button" onClick={detect} disabled={detecting} data-testid="setup-detect-location-button"
              className="w-full inline-flex items-center justify-center gap-2 border border-reveal-rose/40 text-reveal-cream py-3 rounded-full hover:bg-reveal-rose/10 transition-colors disabled:opacity-60">
              <MapPin className="w-4 h-4" /> {detecting ? st.detecting : st.detect}
            </button>
            <input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder={st.cityPlaceholder} data-testid="setup-city-input" className={field} />
            <input value={form.country_name} onChange={(e) => set("country_name", e.target.value)} placeholder={st.country} data-testid="setup-country-input" className={field} />
          </div>
        )}

        {key === "gender" && (
          <div className="flex flex-wrap gap-2" data-testid="setup-gender">
            {GENDER_VALUES.map((g) => (
              <button key={g} type="button" onClick={() => set("gender", g)} data-testid={`setup-gender-${g}`}
                className={`px-5 py-3 rounded-full text-sm transition-colors ${form.gender === g ? "bg-reveal-rose text-black" : "border border-reveal-rose/25 text-reveal-cream/75 hover:border-reveal-rose/50"}`}>
                {s.genderLabels[g]}
              </button>
            ))}
          </div>
        )}

        {key === "lookingFor" && (
          <div className="flex flex-wrap gap-2" data-testid="setup-lookingfor">
            {LOOKING_VALUES.map((g) => (
              <button key={g} type="button" onClick={() => set("looking_for", g)} data-testid={`setup-looking-${g}`}
                className={`px-5 py-3 rounded-full text-sm transition-colors ${form.looking_for === g ? "bg-reveal-rose text-black" : "border border-reveal-rose/25 text-reveal-cream/75 hover:border-reveal-rose/50"}`}>
                {s.lookingLabels[g]}
              </button>
            ))}
          </div>
        )}

        {key === "ageRange" && (
          <div className="flex items-center gap-3">
            <select value={form.min_age} onChange={(e) => set("min_age", e.target.value)} data-testid="setup-minage-select" className={`${field} cursor-pointer`}>
              {AGES.map((a) => <option key={a} value={a} className="text-black">{a === 65 ? px.plus : a}</option>)}
            </select>
            <span className="text-reveal-cream/50 text-sm">{px.to}</span>
            <select value={form.max_age} onChange={(e) => set("max_age", e.target.value)} data-testid="setup-maxage-select" className={`${field} cursor-pointer`}>
              {AGES.map((a) => <option key={a} value={a} className="text-black">{a === 65 ? px.plus : a}</option>)}
            </select>
          </div>
        )}

        {key === "locationPref" && (
          <div className="space-y-3" data-testid="setup-locationpref">
            {LOCATION_PREFS.map((p) => {
              const opt = s.locationPrefOptions[p];
              const on = form.location_pref === p;
              return (
                <button key={p} type="button" onClick={() => set("location_pref", p)} data-testid={`setup-locpref-${p}`}
                  className={`w-full text-left px-5 py-3.5 rounded-2xl border transition-colors flex items-center justify-between ${on ? "bg-reveal-rose/15 border-reveal-rose" : "bg-reveal-burgundy/40 border-reveal-rose/20 hover:border-reveal-rose/50"}`}>
                  <span><span className="text-reveal-cream text-sm">{opt.label}</span><span className="text-reveal-cream/45 text-xs block">{opt.desc}</span></span>
                  {on && <Check className="w-4 h-4 text-reveal-rose" />}
                </button>
              );
            })}
          </div>
        )}

        {key === "bio" && (
          <textarea rows={5} value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder={st.placeholder} data-testid="setup-bio-input"
            className={`${field} resize-none`} />
        )}

        <div className="flex items-center justify-between mt-9">
          <button onClick={back} disabled={step === 0} data-testid="setup-back-button"
            className="inline-flex items-center gap-2 text-sm text-reveal-cream/60 hover:text-reveal-cream transition-colors disabled:opacity-0">
            <ArrowLeft className="w-4 h-4" /> {s.back}
          </button>
          <button onClick={next} disabled={!canProceed() || saving} data-testid="setup-next-button"
            className="inline-flex items-center gap-2 bg-reveal-rose text-black font-medium px-8 py-3 rounded-full hover:bg-[#c9987b] transition-colors disabled:opacity-40">
            {saving ? s.submitting : step === STEPS.length - 1 ? s.finish : s.next}
            {!saving && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
